<template>
  <div class="ml-12 mr-12 my-2 members-container">
    <!-- Notification Dialog -->
    <NotificationDialog
      v-model:visible="showNotification"
      :type="notificationType"
      :title="notificationTitle"
      :message="notificationMessage"
      @close="showNotification = false"
    />

    <!-- Confirmation Dialog -->
    <ConfirmationDialog
      v-model:visible="showConfirmationDialog"
      :title="'Delete Member'"
      :message="`Are you sure you want to delete ${itemToDelete?.firstName} ${itemToDelete?.surname}? This action cannot be undone.`"
      @confirm="deleteMember"
      @cancel="showConfirmationDialog = false"
    />



    <!-- Page Header -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card class="pa-4" style="border-radius: 15px; background: linear-gradient(135deg, #002060 0%, #001547 100%);">
          <h2 class="text-white mb-2">{{ getPageTitle() }}</h2>
          <p class="text-white text-opacity-80 mb-0">
            {{ getPageDescription() }}
          </p>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters Row -->
    <v-row class="mb-4">
      <!-- Search input -->
      <v-col cols="12" sm="6" md="2">
        <v-text-field
          v-model="search"
          label="Search Members"
          clearable
          class="elevation-0"
          @input="debouncedSearch"
          density="comfortable"
          bg-color="#e8e8e8"
          flat
          rounded
          append-inner-icon="mdi-magnify"
          variant="solo"
        />
      </v-col>

      <!-- Member Type filter -->
      <v-col cols="12" sm="6" md="2">
        <v-select
          v-model="memberTypeFilter"
          :items="memberTypeOptions"
          label="Filter by Type"
          clearable
          density="comfortable"
          bg-color="#e8e8e8"
          flat
          rounded
          variant="solo"
          @update:model-value="debouncedSearch"
        />
      </v-col>

      <!-- Membership Type filter -->
      <v-col cols="12" sm="6" md="2">
        <v-select
          v-model="membershipTypeFilter"
          :items="membershipTypeOptions"
          label="Filter by Membership"
          clearable
          density="comfortable"
          bg-color="#e8e8e8"
          flat
          rounded
          variant="solo"
          @update:model-value="debouncedSearch"
        />
      </v-col>



      <!-- Invite Member button - Only show on pending-invite page or default page -->
      <v-col cols="12" sm="6" md="4" class="d-flex align-center justify-start" v-if="!$route.query.type || $route.query.type === 'pending-invite'">
        <v-btn
          color="#002060"
          class="text-white"
          @click="$router.push('/invite-member')"
          prepend-icon="mdi-plus"
          rounded
          flat
        >
          Invite Member
        </v-btn>
      </v-col>
    </v-row>





    <!-- Tabs - Only show if no specific type is selected -->
    <v-row class="mb-4" v-if="!$route.query.type || $route.query.type === ''">
      <v-col cols="12">
        <v-tabs
          v-model="activeTab"
          color="#002060"
          class="mb-4"
        >
          <v-tab value="pending">Pending Invites</v-tab>
          <v-tab value="new">New Members</v-tab>
        </v-tabs>
      </v-col>
    </v-row>

    <!-- Dynamic Content based on type or tabs -->
    <div v-if="$route.query.type && $route.query.type !== '' && $route.query.type !== 'pending-invite'">
      <!-- Single view based on type -->
      <v-row>
        <v-col cols="12">
          <v-data-table
            :headers="getTableHeaders() || []"
            :items="getFilteredData() || []"
            :items-per-page="10"
            :loading="loading"
            style="border-radius: 17px;"
            loading-text="Loading... Please wait"
            class="custom-header elevation-3"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :footer-props="{ 'items-per-page-options': [5,10,15,20] }"
          >
            <template v-slot:item.memberType="{ item }">
              <v-chip
                :color="getMemberTypeColor(item.memberType)"
                text-color="white"
                small
              >
                {{ item.memberType || 'Individual' }}
              </v-chip>
            </template>

            <template v-slot:item.membershipType="{ item }">
              <v-chip
                :color="getMembershipTypeColor(item.membershipType)"
                text-color="white"
                small
              >
                {{ item.membershipType || 'Individual' }}
              </v-chip>
            </template>

            <template v-slot:item.status="{ item }">
              <v-chip
                :color="getStatusColor(item.status)"
                text-color="white"
                small
              >
                {{ item.status || 'Pending' }}
              </v-chip>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn 
                variant="text" 
                @click.stop="viewInvite(item)"
                color="#002060"
                size="small"
                class="mr-1"
              >
                View
              </v-btn>
            </template>
          </v-data-table>
        </v-col>
      </v-row>


    </div>

    <!-- Pending Invite Content (Single View) -->
    <div v-if="$route.query.type === 'pending-invite'">
      <!-- Combined data table showing both pending and completed -->
      <v-row v-if="getCombinedInviteData().length > 0">
        <v-col cols="12">
          <v-data-table
            :headers="getCombinedHeaders()"
            :items="getCombinedInviteData()"
            :items-per-page="10"
            :loading="loading"
            style="border-radius: 17px;"
            loading-text="Loading... Please wait"
            class="custom-header elevation-3"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :footer-props="{ 'items-per-page-options': [5,10,15,20] }"
          >
            <template v-slot:item.memberType="{ item }">
              <v-chip
                :color="getMemberTypeColor(item.memberType)"
                text-color="white"
                small
              >
                {{ item.memberType || 'Individual' }}
              </v-chip>
            </template>

            <template v-slot:item.membershipType="{ item }">
              <v-chip
                :color="getMembershipTypeColor(item.membershipType)"
                text-color="white"
                small
              >
                {{ item.membershipType || 'Individual' }}
              </v-chip>
            </template>

            <template v-slot:item.paymentStatus="{ item }">
              <v-chip
                :color="getPaymentStatusColor(item)"
                text-color="white"
                small
              >
                {{ getPaymentStatusText(item) }}
              </v-chip>
            </template>

            <template v-slot:item.status="{ item }">
              <v-chip
                :color="getStatusColor(item.status)"
                text-color="white"
                small
              >
                {{ item.status || 'Pending' }}
              </v-chip>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn 
                variant="text" 
                @click.stop="item.isPending ? viewInvite(item) : viewNewMember(item)"
                color="#002060"
                size="small"
                class="mr-1"
              >
                View
              </v-btn>
              <v-btn 
                v-if="item.isPending"
                variant="text" 
                @click.stop="resendInvite(item)"
                color="info"
                size="small"
                class="mr-1"
              >
                Resend
              </v-btn>
            </template>
          </v-data-table>
        </v-col>
      </v-row>

      <!-- No data message -->
      <v-row v-else-if="!loading">
        <v-col cols="12">
          <div class="no-invites-container">
            <v-icon size="64" color="#ccc">mdi-email-outline</v-icon>
            <h3 class="no-invites-title">No Invite/Pending Members</h3>
            <p class="no-invites-message">There are currently no pending invitations or completed members.</p>
            <v-btn
              color="#002060"
              dark
              rounded
              @click="navigateToInviteMember"
            >
              <v-icon left>mdi-email-plus</v-icon>
              Invite Member
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- Tabbed Content (original behavior when no type is specified) -->
    <v-window v-if="!$route.query.type || $route.query.type === ''" v-model="activeTab">
      <v-window-item value="pending">
        <!-- Pending Invites table -->
        <v-row v-if="filteredPendingInvites.length > 0">
          <v-col cols="12">
            <v-data-table
              :headers="pendingHeaders"
              :items="filteredPendingInvites"
              :items-per-page="10"
              :loading="loading"
              style="border-radius: 17px;"
              loading-text="Loading... Please wait"
              class="custom-header elevation-3"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :footer-props="{ 'items-per-page-options': [5,10,15,20] }"
            >
                <template v-slot:item.memberType="{ item }">
                  <v-chip
                    :color="getMemberTypeColor(item.memberType)"
                    text-color="white"
                    small
                  >
                    {{ item.memberType || 'Individual' }}
                  </v-chip>
                </template>

                <template v-slot:item.membershipType="{ item }">
                  <v-chip
                    :color="getMembershipTypeColor(item.membershipType)"
                    text-color="white"
                    small
                  >
                    {{ item.membershipType || 'Individual' }}
                  </v-chip>
                </template>

                <template v-slot:item.status="{ item }">
                  <v-chip
                    :color="getStatusColor(item.status)"
                    text-color="white"
                    small
                  >
                    {{ item.status || 'Pending' }}
                  </v-chip>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-btn 
                    variant="text" 
                    @click.stop="viewInvite(item)"
                    color="#002060"
                    size="small"
                    class="mr-1"
                  >
                    View
                  </v-btn>
                  <v-btn 
                    variant="text" 
                    @click.stop="resendInvite(item)"
                    color="info"
                    size="small"
                    class="mr-1"
                  >
                    Resend
                  </v-btn>
                </template>
              </v-data-table>
            </v-col>
          </v-row>

          <!-- No pending invites message -->
          <v-row v-else-if="!loading">
            <v-col cols="12">
              <div class="no-invites-container">
                <v-icon size="64" color="#ccc">mdi-email-outline</v-icon>
                <h3 class="no-invites-title">No Pending Invitations</h3>
                <p class="no-invites-message">There are currently no pending member invitations.</p>
                <v-btn
                  color="#002060"
                  dark
                  rounded
                  @click="navigateToInviteMember"
                >
                  <v-icon left>mdi-email-plus</v-icon>
                  Invite Member
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-window-item>

        <!-- New Members Tab Content -->
        <v-window-item value="new">
          <!-- New Members table -->
          <v-row v-if="filteredNewMembers.length > 0">
            <v-col cols="12">
              <v-data-table
                :headers="newMembersHeaders"
                :items="filteredNewMembers"
                :items-per-page="10"
                :loading="loading"
                style="border-radius: 17px;"
                loading-text="Loading... Please wait"
                class="custom-header elevation-3"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :footer-props="{ 'items-per-page-options': [5,10,15,20] }"
              >
                <template v-slot:item.memberType="{ item }">
                  <v-chip
                    :color="getMemberTypeColor(item.memberType)"
                    text-color="white"
                    small
                  >
                    {{ item.memberType || 'Individual' }}
                  </v-chip>
                </template>

                <template v-slot:item.membershipType="{ item }">
                  <v-chip
                    :color="getMembershipTypeColor(item.membershipType)"
                    text-color="white"
                    small
                  >
                    {{ item.membershipType || 'Individual' }}
                  </v-chip>
                </template>

                <template v-slot:item.status="{ item }">
                  <v-chip
                    :color="getNewMemberStatusColor(item.status)"
                    text-color="white"
                    small
                  >
                    {{ item.status || 'Active' }}
                  </v-chip>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-btn 
                    variant="text" 
                    @click.stop="viewNewMember(item)"
                    color="#002060"
                    size="small"
                    class="mr-1"
                  >
                    View
                  </v-btn>

                </template>
              </v-data-table>
            </v-col>
          </v-row>

          <!-- No new members message -->
          <v-row v-else-if="!loading">
            <v-col cols="12">
              <div class="no-invites-container">
                <v-icon size="64" color="#ccc">mdi-account-plus</v-icon>
                <h3 class="no-invites-title">No New Members</h3>
                <p class="no-invites-message">There are currently no newly added members.</p>
              </div>
            </v-col>
          </v-row>
        </v-window-item>
      </v-window>
    </div>
  </template>
  
  <script>
  import { debounce } from 'lodash';
import { db } from '../firebaseConfig';
import { collection, onSnapshot, query, orderBy, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ActivityLogger } from '../utils/activityLogger';
import { mapGetters } from 'vuex';
import NotificationDialog from '../components/NotificationDialog.vue';
import ConfirmationDialog from '../components/ConfirmationDialog.vue';

export default {
  components: {
    NotificationDialog,
    ConfirmationDialog
  },
  computed: {
    ...mapGetters(['user'])
  },
  data() {
    return {
      showNotification: false,
      notificationType: 'success',
      notificationTitle: '',
      notificationMessage: '',
      showConfirmationDialog: false,
      itemToDelete: null,
      search: '',
      memberTypeFilter: null,
      membershipTypeFilter: null,
      loading: false,
      pendingInvites: [],
      completedInvites: [],
      newMembers: [],
      activeTab: 'pending',
      pendingInviteTab: 'pending',
      unsubscribeInvites: null,
      unsubscribeCompletedInvites: null,
      unsubscribeNewMembers: null,
      pendingHeaders: [
        { title: 'Name',             key: 'firstName',          sortable: true,  align: 'center' },
        { title: 'Surname',          key: 'surname',            sortable: true,  align: 'center' },
        { title: 'Email',            key: 'email',              sortable: true,  align: 'center' },
        { title: 'Phone',            key: 'phone',              sortable: true,  align: 'center' },
        { title: 'Membership Type',  key: 'membershipType',     sortable: true,  align: 'center' },
        { title: 'Payment Status',   key: 'paymentStatus',      sortable: true,  align: 'center' },
        { title: 'Date Added',       key: 'dateAdded',          sortable: true,  align: 'center' },
        { title: 'Actions',          key: 'actions',            sortable: false, align: 'center' },
      ],
              newMembersHeaders: [
          { title: 'Member ID',        key: 'clientId',           sortable: true,  align: 'center' },
          { title: 'Name',             key: 'firstName',          sortable: true,  align: 'center' },
          { title: 'Surname',          key: 'surname',            sortable: true,  align: 'center' },
          { title: 'Email',            key: 'email',              sortable: true,  align: 'center' },
          { title: 'Membership Type',  key: 'membershipType',     sortable: true,  align: 'center' },
          { title: 'Date Added',       key: 'dateAdded',          sortable: true,  align: 'center' },
          { title: 'Actions',          key: 'actions',            sortable: false, align: 'center' },
        ],
      sortBy: ['dateAdded'],
      sortDesc: [false],
      memberTypeOptions: [
        { title: 'All Types', value: null },
        { title: 'Individual Members', value: 'Individual' },
        { title: 'Business Members', value: 'Business' }
      ],
      individualMembershipTypes: [
        'Domestic',
        'Individual',
        'Individual & Domestic'
      ],
      businessMembershipTypes: [
        'Blue Labour',
        'Silver Labour',
        'Gold Labour'
      ]
    };
  },
    computed: {
      filteredPendingInvites() {
        const filtered = this.pendingInvites.filter(invite => {
          const matchesSearch = this.search
            ? [invite.firstName, invite.surname, invite.email, invite.memberType, invite.membershipType, invite.status]
                .filter(Boolean)
                .some(f => f.toString().toLowerCase().includes(this.search.toLowerCase()))
            : true;
          const matchesMemberType = this.memberTypeFilter
            ? invite.memberType === this.memberTypeFilter
            : true;
          const matchesMembershipType = this.membershipTypeFilter
            ? invite.membershipType === this.membershipTypeFilter
            : true;
          const matchesStatus = this.statusFilter
            ? invite.status === this.statusFilter
            : true;
          return matchesSearch && matchesMemberType && matchesMembershipType && matchesStatus;
        });
        console.log('Filtered pending invites:', filtered.length, filtered);
        return filtered;
      },
      filteredNewMembers() {
        return this.newMembers.filter(member => {
          const matchesSearch = this.search
            ? [member.firstName, member.surname, member.email, member.memberType, member.membershipType, member.status]
                .filter(Boolean)
                .some(f => f.toString().toLowerCase().includes(this.search.toLowerCase()))
            : true;
          const matchesMemberType = this.memberTypeFilter
            ? member.memberType === this.memberTypeFilter
            : true;
          const matchesMembershipType = this.membershipTypeFilter
            ? member.membershipType === this.membershipTypeFilter
            : true;
          const matchesStatus = this.statusFilter
            ? member.status === this.statusFilter
            : true;
          return matchesSearch && matchesMemberType && matchesMembershipType && matchesStatus;
        });
      },
      filteredCompletedInvites() {
        return this.completedInvites.filter(invite => {
          const matchesSearch = this.search
            ? [invite.firstName, invite.surname, invite.email, invite.memberType, invite.membershipType, invite.status]
                .filter(Boolean)
                .some(f => f.toString().toLowerCase().includes(this.search.toLowerCase()))
            : true;
          const matchesMemberType = this.memberTypeFilter
            ? invite.memberType === this.memberTypeFilter
            : true;
          const matchesMembershipType = this.membershipTypeFilter
            ? invite.membershipType === this.membershipTypeFilter
            : true;
          const matchesStatus = this.statusFilter
            ? invite.status === this.statusFilter
            : true;
          return matchesSearch && matchesMemberType && matchesMembershipType && matchesStatus;
        });
      }
    },
    methods: {
      getPendingInvites() {
        this.loading = true;
        // Listen for real-time updates from Firestore - pending invites collection
        if (this.unsubscribeInvites) this.unsubscribeInvites();
        const invitesQuery = query(
          collection(db, 'pendingInvites'), 
          orderBy('dateAdded', 'desc')
        );
        this.unsubscribeInvites = onSnapshot(invitesQuery, (snapshot) => {
          this.pendingInvites = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log('Pending invites loaded:', this.pendingInvites.length, this.pendingInvites);
          this.loading = false;
        }, (error) => {
          console.error('Firestore error:', error);
          this.pendingInvites = [];
          this.loading = false;
        });
      },

      getCompletedInvites() {
        // Listen for real-time updates from Firestore - completed invites collection
        if (this.unsubscribeCompletedInvites) this.unsubscribeCompletedInvites();
        const completedInvitesQuery = query(
          collection(db, 'completedInvites'), 
          orderBy('completedAt', 'desc')
        );
        this.unsubscribeCompletedInvites = onSnapshot(completedInvitesQuery, (snapshot) => {
          this.completedInvites = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log('Completed invites loaded:', this.completedInvites.length, this.completedInvites);
        }, (error) => {
          console.error('Firestore error:', error);
          this.completedInvites = [];
        });
      },

      navigateToInviteMember() {
        this.$router.push({ name: 'InviteMember' });
      },

      viewInvite(item) {
        console.log('Viewing invite:', item);
        // For completed invites, we need to use the originalInviteId if available
        const inviteId = item.originalInviteId || item.id;
        this.$router.push({ name: 'InviteDetails', params: { id: inviteId } });
      },

      async resendInvite(item) {
        try {
          if (!item || !item.email) {
            this.notificationType = 'error';
            this.notificationTitle = 'Error!';
            this.notificationMessage = 'No email available for this invite.';
            this.showNotification = true;
            return;
          }

          // Prevent resending if this invite is already completed
          const emailLower = (item.email || '').trim().toLowerCase();
          const alreadyCompleted = (this.completedInvites || []).some(ci => {
            const ciEmail = (ci.email || '').trim().toLowerCase();
            return (ci.originalInviteId && ci.originalInviteId === item.id) || (ciEmail && ciEmail === emailLower) || (ci.status && ci.status === 'Client Completed' && ciEmail === emailLower);
          });

          if (alreadyCompleted || item.status === 'Client Completed') {
            this.notificationType = 'info';
            this.notificationTitle = 'Already Completed';
            this.notificationMessage = 'This registration is already completed. No resend necessary.';
            this.showNotification = true;
            return;
          }

          this.loading = true;

          const recipientFullName = (item.memberType === 'Business')
            ? (item.businessName || 'Member')
            : `${item.firstName || ''} ${item.surname || ''}`.trim() || 'Member';

          const response = await fetch(
            'https://sendinviteemail-zjdy2tr2aa-uc.a.run.app',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                to: item.email,
                subject: 'Welcome to the DMM Law Club - Your Invitation',
                recipientFullName,
                referredByName: item.referredByName || '',
                companyName: item.companyName || ''
              })
            }
          );

          if (!response.ok) {
            const text = await response.text();
            console.error('Resend failed:', response.status, text);
            this.notificationType = 'error';
            this.notificationTitle = 'Error!';
            this.notificationMessage = 'Failed to resend invite. Please try again.';
            this.showNotification = true;
            return;
          }

          // Log activity
          const invitedBy = this.user?.displayName || this.user?.email?.split('@')[0] || 'Unknown';
          await ActivityLogger.memberInvited(invitedBy, this.user?.uid, `Resent invite - ${item.email}`, 'Members Page');

          this.notificationType = 'success';
          this.notificationTitle = 'Success!';
          this.notificationMessage = `Invite resent to ${recipientFullName} successfully!`;
          this.showNotification = true;
        } catch (e) {
          console.error('Error resending invite:', e);
          this.notificationType = 'error';
          this.notificationTitle = 'Error!';
          this.notificationMessage = 'Failed to resend invite. Please try again.';
          this.showNotification = true;
        } finally {
          this.loading = false;
        }
      },

      getMemberTypeColor(memberType) {
        const colors = {
          'Individual': 'blue',
          'Business': 'purple'
        };
        return colors[memberType] || 'blue';
      },

      getMembershipTypeColor(membershipType) {
        const colors = {
          'Domestic': 'green',
          'Individual': 'blue',
          'Individual & Domestic': 'purple'
        };
        return colors[membershipType] || 'green';
      },

      getStatusColor(status) {
        const colors = {
          'Pending': 'warning',
          'Client Completed': 'success'
        };
        return colors[status] || 'grey';
      },

      getPaymentStatusText(item) {
        // Check for subscription active flag
        if (item.subscriptionActive === true) {
          return 'Paid';
        }
      
        // Check status field
        const status = (item.status || '').toLowerCase();
        if (status.includes('payment completed') || status.includes('subscription active')) {
          return 'Paid';
        }
      
        // Check payfast object
        if (item.payfast && item.payfast.payment_status) {
          const pfStatus = item.payfast.payment_status.toUpperCase();
          if (pfStatus === 'COMPLETE' || pfStatus === 'SUBSCRIPTION_ACTIVE') {
            return 'Paid';
          }
        }
      
        return 'Awaiting Payment';
      },

      getPaymentStatusColor(item) {
        const statusText = this.getPaymentStatusText(item);
        return statusText === 'Paid' ? 'success' : 'warning';
      },

      showDeleteConfirmation(item) {
        this.itemToDelete = item;
        this.showConfirmationDialog = true;
      },

      async confirmDelete() {
        if (!this.itemToDelete) return;
        
        try {
          await deleteDoc(doc(db, 'pendingInvites', this.itemToDelete.id));
          console.log(`Pending invite ${this.itemToDelete.id} deleted successfully`);
      
          // Log invite deletion activity
          const deletedBy = this.user?.displayName || this.user?.email?.split('@')[0] || 'Unknown';
          await ActivityLogger.userDeleted(deletedBy, this.user?.uid, `${this.itemToDelete.firstName} ${this.itemToDelete.surname}`);
          
          // Show success notification
          this.notificationType = 'success';
          this.notificationTitle = 'Success!';
          this.notificationMessage = `Pending invite for ${this.itemToDelete.firstName} ${this.itemToDelete.surname} deleted successfully!`;
          this.showNotification = true;
        } catch (error) {
          console.error('Error deleting pending invite:', error);
          
          // Show error notification
          this.notificationType = 'error';
          this.notificationTitle = 'Error!';
          this.notificationMessage = 'Failed to delete pending invite. Please try again.';
          this.showNotification = true;
        }
      },

      cancelDelete() {
        this.itemToDelete = null;
        this.showConfirmationDialog = false;
      },

      viewNewMember(item) {
        console.log('Viewing member:', item);
        // If this is a completed invite, view it as an invite, otherwise as a member
        if (item.status === 'Client Completed') {
          const inviteId = item.originalInviteId || item.id;
          this.$router.push({ name: 'InviteDetails', params: { id: inviteId } });
        } else {
          this.$router.push({ name: 'MemberDetails', params: { id: item.id } });
        }
      },



      getNewMemberStatusColor(status) {
        const colors = {
          'Active': 'success',
          'Inactive': 'error',
          'Pending': 'warning',
          'Newly Added Member': 'info'
        };
        return colors[status] || 'success';
      },

      getNewMembers() {
        this.loading = true;
        // Listen for real-time updates from Firestore - new members collection
        if (this.unsubscribeNewMembers) this.unsubscribeNewMembers();
        const newMembersQuery = query(
          collection(db, 'newMembers'), 
          orderBy('dateAdded', 'desc')
        );
        this.unsubscribeNewMembers = onSnapshot(newMembersQuery, (snapshot) => {
          this.newMembers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log('New members loaded:', this.newMembers.length, this.newMembers);
          this.loading = false;
        }, (error) => {
          console.error('Firestore error:', error);
          this.newMembers = [];
          this.loading = false;
        });
      },

      debouncedSearch() {
        this.loading = true;
        setTimeout(() => {
          this.loading = false;
        }, 500); // Adjust debounce time as needed
      },

      getPageTitle() {
        const type = this.$route.query.type || '';
        switch (type) {
          case 'pending-invite':
            return 'Invite/Pending Members';
          case 'individual':
            return 'Individual Members';
          case 'business':
            return 'Business Members';
          default:
            return 'Member Management';
        }
      },

      getPageDescription() {
        const type = this.$route.query.type || '';
        switch (type) {
          case 'pending-invite':
            return 'Manage pending member invitations and completed registrations';
          case 'individual':
            return 'Manage individual member information and status';
          case 'business':
            return 'Manage business member information and status';
          default:
            return 'Manage member invitations, registrations, and membership types';
        }
      },

      getFilteredData() {
        const type = this.$route.query.type || '';
        if (!type) {
          // Return data based on active tab
          return this.activeTab === 'pending' ? this.filteredPendingInvites : this.filteredNewMembers;
        }

        // Filter data based on type
        let result;
        switch (type) {
          case 'pending-invite':
            // Show ONLY pending invites from pendingInvites collection
            result = this.filteredPendingInvites || [];
            break;
          case 'individual':
            // Show ONLY individual members from completedInvites collection
            result = (this.completedInvites || []).filter(invite => invite.memberType === 'Individual');
            break;
          case 'business':
            // Show ONLY business members from completedInvites collection
            result = (this.completedInvites || []).filter(invite => invite.memberType === 'Business');
            break;
          default:
            result = this.filteredPendingInvites || [];
        }
        
        console.log(`getFilteredData for type '${type}':`, result.length, result);
        return result;
      },

      getTableHeaders() {
        const type = this.$route.query.type || '';
        if (!type) {
          // Return headers based on active tab
          return this.activeTab === 'pending' ? this.pendingHeaders : this.newMembersHeaders;
        }

        // Return headers based on type
        switch (type) {
          case 'pending-invite':
            return this.pendingHeaders || [];
          case 'individual':
          case 'business':
            return this.newMembersHeaders || [];
          default:
            return this.pendingHeaders || [];
        }
      },

      getNoDataIcon() {
        const type = this.$route.query.type || '';
        switch (type) {
          case 'pending-invite':
            return 'mdi-email-outline';
          case 'individual':
            return 'mdi-account-check';
          case 'business':
            return 'mdi-domain-check';
          default:
            return 'mdi-account-group';
        }
      },

      getNoDataTitle() {
        const type = this.$route.query.type || '';
        switch (type) {
          case 'pending-invite':
            return 'No Pending Invitations';
          case 'individual':
            return 'No Individual Members';
          case 'business':
            return 'No Business Members';
          default:
            return 'No Members Found';
        }
      },

      getNoDataMessage() {
        const type = this.$route.query.type || '';
        switch (type) {
          case 'pending-invite':
            return 'There are currently no pending invitations or completed members.';
          case 'individual':
            return 'There are currently no completed individual member registrations.';
          case 'business':
            return 'There are currently no completed business member registrations.';
          default:
            return 'No members found matching the current criteria.';
        }
      },

      getCombinedInviteData() {
        // Show ONLY pending invites for the pending-invite view
        const pendingInvites = this.filteredPendingInvites.map(item => ({
          ...item,
          isPending: true
        }));
        
        console.log('Combined invite data (pending only):', pendingInvites.length, pendingInvites);
        return pendingInvites;
      },

      getCombinedHeaders() {
        // Use the pending headers as base, but ensure we have all necessary columns
        return this.pendingHeaders || [];
      },

      async deleteMember() {
        if (!this.itemToDelete) return;

        const type = this.$route.query.type || '';
        let collectionName, itemType;

        if (type === 'pending-invite') {
          // For pending-invite view, check if item is pending or completed
          if (this.itemToDelete.isPending) {
            collectionName = 'pendingInvites';
            itemType = 'pending invite';
          } else {
            collectionName = 'completedInvites';
            itemType = 'completed member';
          }
        } else {
          // For individual/business views
          const isCompletedInvite = type === 'individual' || type === 'business';
          collectionName = isCompletedInvite ? 'completedInvites' : 'pendingInvites';
          itemType = isCompletedInvite ? 'completed member' : 'pending invite';
        }

        try {
          await deleteDoc(doc(db, collectionName, this.itemToDelete.id));
          console.log(`${itemType} ${this.itemToDelete.id} deleted successfully`);

          // Log deletion activity
          const deletedBy = this.user?.displayName || this.user?.email?.split('@')[0] || 'Unknown';
          await ActivityLogger.userDeleted(deletedBy, this.user?.uid, `${this.itemToDelete.firstName} ${this.itemToDelete.surname}`);

          // Show success notification
          this.notificationType = 'success';
          this.notificationTitle = 'Success!';
          this.notificationMessage = `${itemType} for ${this.itemToDelete.firstName} ${this.itemToDelete.surname} deleted successfully!`;
          this.showNotification = true;
        } catch (error) {
          console.error(`Error deleting ${itemType}:`, error);

          // Show error notification
          this.notificationType = 'error';
          this.notificationTitle = 'Error!';
          this.notificationMessage = `Failed to delete ${itemType}. Please try again.`;
          this.showNotification = true;
        } finally {
          this.showConfirmationDialog = false;
          this.itemToDelete = null;
        }
      }
    },
    created() {
      this.getPendingInvites();
      this.getCompletedInvites();
      this.getNewMembers();
    },
    beforeUnmount() {
      if (this.unsubscribeInvites) this.unsubscribeInvites();
      if (this.unsubscribeCompletedInvites) this.unsubscribeCompletedInvites();
      if (this.unsubscribeNewMembers) this.unsubscribeNewMembers();
    }
  };
  </script>
  
  <style scoped>
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');
  
  * {
    font-family: 'Montserrat', sans-serif;
  }
  
.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #002060;
  margin: 0 0 0.5rem 0;
}

.page-subtitle {
  font-size: 0.875rem;
  color: #666;
  margin: 0 0 1rem 0;
}

  .custom-header {
    background-color: #e8e8e8;
  }

  .no-invites-container {
  text-align: center;
  padding: 3rem 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px dashed #d1d5db;
}

.no-invites-title {
  font-size: 1.25rem;
  color: #666;
  margin: 1rem 0 0.75rem 0;
  font-weight: 600;
}

.no-invites-message {
  font-size: 0.875rem;
  color: #888;
  margin: 0 0 1.5rem 0;
}

  .status-toggle-btn {
    transition: all 0.2s ease;
  }

  .status-toggle-btn:hover {
    transform: scale(1.1);
  }

  /* Custom tooltip styling */
  :deep(.v-tooltip .v-overlay__content) {
    background-color: #002060 !important;
    color: white !important;
    font-size: 0.85rem !important;
    padding: 8px 12px !important;
    border-radius: 6px !important;
    box-shadow: 0 4px 12px rgba(0, 32, 96, 0.3) !important;
  }
  </style>
  
  